class HeaderRedesign extends HTMLElement {
  constructor() {
    super();
    this.openTimer = null;
    this.closeTimer = null;
    this.activePanel = null;
    this.activeTrigger = null;
    this.mobileDrawer = null;
    this.collectionProductCache = new Map();
    this.preloadedProductImages = new Set();
    this.collectionPrefetchStarted = false;
  }

  connectedCallback() {
    this.ensureVectorControls();
    this.desktopTriggers = [...this.querySelectorAll('[data-hr-panel-trigger]')];
    this.panels = [...this.querySelectorAll('[data-hr-panel]')];
    this.backdrop = this.querySelector('[data-hr-backdrop]');
    this.mobileDrawer = this.querySelector('[data-hr-mobile-drawer]');

    this.desktopTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => this.scheduleOpen(trigger));
      trigger.addEventListener('mouseleave', () => this.scheduleClose());
      trigger.addEventListener('focus', () => this.openPanel(trigger));
      trigger.addEventListener('click', () => {
        if (this.activeTrigger === trigger) this.closePanel(true);
        else this.openPanel(trigger);
      });
    });

    this.panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => this.cancelClose());
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    });

    this.backdrop?.addEventListener('click', () => this.closePanel(true));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closePanel(false);
    });

    this.bindProducerTabs();
    this.bindCollectionTabs();
    this.addEventListener('mouseenter', () => this.prefetchCollectionProducts(), { once: true });
    this.addEventListener('focusin', () => this.prefetchCollectionProducts(), { once: true });
    this.bindAddToCart();
    this.bindSliders();
    this.bindCountrySelectors();
    this.bindStickyReveal();
    this.bindMobile();
  }

  // Smart sticky header: hides on scroll down, reveals as soon as the user scrolls up.
  // The whole section (not only <header-redesign>) is sticky, see header-redesign.css.
  bindStickyReveal() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    this.stickySection = section;
    section.classList.add('hr-sticky-section');

    const root = document.documentElement;
    const setOffset = () => {
      const visible = !section.classList.contains('hr-sticky-section--hidden');
      root.style.setProperty('--hr-header-height', `${section.offsetHeight}px`);
      root.style.setProperty('--hr-header-offset', visible ? `${section.offsetHeight}px` : '0px');
    };
    this.setStickyHidden = (hidden) => {
      if (section.classList.contains('hr-sticky-section--hidden') === hidden) return;
      section.classList.toggle('hr-sticky-section--hidden', hidden);
      setOffset();
    };

    const threshold = 8;
    let lastY = Math.max(0, window.scrollY);
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const maxY = Math.max(0, root.scrollHeight - window.innerHeight);
      if (y > maxY) return; // iOS bounce at the bottom of the page
      if (this.isHeaderBusy() || y <= section.offsetHeight * 2) {
        this.setStickyHidden(false);
        lastY = y;
        return;
      }
      const delta = y - lastY;
      if (Math.abs(delta) < threshold) return;
      this.setStickyHidden(delta > 0);
      lastY = y;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    this.addEventListener('focusin', () => this.setStickyHidden(false));
    if ('ResizeObserver' in window) new ResizeObserver(setOffset).observe(section);
    setOffset();
  }

  isHeaderBusy() {
    return Boolean(
      this.activePanel
      || this.mobileDrawer?.classList.contains('is-open')
      || this.classList.contains('has-open-language')
      || this.querySelector('details[open]')
    );
  }

  createVectorIcon(type, direction = 'right', className = '') {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const isChevron = type === 'chevron';

    svg.setAttribute('class', `hr-icon hr-icon--${type} hr-icon--${direction}${className ? ` ${className}` : ''}`);
    svg.setAttribute('viewBox', isChevron ? '0 0 9 17' : '0 0 14 10');
    svg.setAttribute('width', isChevron ? '9' : '14');
    svg.setAttribute('height', isChevron ? '17' : '10');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('role', 'presentation');

    if (isChevron) {
      path.setAttribute('d', 'M9.08813 7.0015L8.42477 6.33813L4.54407 10.252L0.663368 6.33813L-5.79934e-08 7.0015L4.54407 11.5456L9.08813 7.0015Z');
    } else {
      path.setAttribute('d', 'M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546Z');
      path.setAttribute('fill-rule', 'evenodd');
      path.setAttribute('clip-rule', 'evenodd');
    }
    path.setAttribute('fill', 'currentColor');
    svg.append(path);
    return svg;
  }

  stripArrowCharacters(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
      node.textContent = node.textContent.replace(/[→←›‹]/g, '').trimEnd();
    });
  }

  ensureVectorControls() {
    this.querySelectorAll('.hr-header__nav-trigger').forEach((trigger) => {
      if (!trigger.querySelector('.hr-header__nav-chevron')) {
        trigger.append(this.createVectorIcon('chevron', 'down', 'hr-header__nav-chevron'));
      }
    });

    this.querySelectorAll('.hr-mega-panel__tab').forEach((tab) => {
      this.stripArrowCharacters(tab);
      if (!tab.querySelector('.hr-country-chevron')) {
        tab.append(this.createVectorIcon('chevron', 'right', 'hr-country-chevron'));
      }
    });

    this.querySelectorAll('.hr-producer-overview__heading a, .hr-producer-overview__compact-copy a, .hr-producer-panel__footer a, .hr-article-slider__controls > a').forEach((link) => {
      this.stripArrowCharacters(link);
      link.classList.toggle('hr-inline-arrow-link', link.closest('.hr-article-slider__controls') !== null);
      if (!link.querySelector('.hr-icon--arrow')) link.append(this.createVectorIcon('arrow'));
    });

    this.querySelectorAll('.hr-mobile-submenu a').forEach((link) => {
      if (!/[→←›‹]/.test(link.textContent)) return;
      this.stripArrowCharacters(link);
      link.classList.add('hr-inline-arrow-link');
      if (!link.querySelector('.hr-icon--arrow')) link.append(this.createVectorIcon('arrow'));
    });

    this.querySelectorAll('[data-hr-slider-direction]').forEach((button) => {
      const direction = Number(button.dataset.hrSliderDirection) < 0 ? 'left' : 'right';
      if (!button.querySelector('.hr-icon')) {
        button.replaceChildren(this.createVectorIcon('arrow', direction));
      }
    });

    this.querySelectorAll('[data-mobile-back-main], [data-mobile-back-countries]').forEach((button) => {
      button.replaceChildren(this.createVectorIcon('arrow', 'left'));
    });

    this.querySelectorAll('.hr-mobile-country-button').forEach((button) => {
      this.stripArrowCharacters(button);
      if (!button.querySelector('.hr-mobile-country-chevron')) {
        button.append(this.createVectorIcon('chevron', 'right', 'hr-mobile-country-chevron'));
      }
    });

  }

  scheduleOpen(trigger) {
    this.cancelClose();
    window.clearTimeout(this.openTimer);
    if (this.activePanel) this.openPanel(trigger);
    else this.openTimer = window.setTimeout(() => this.openPanel(trigger), 150);
  }

  scheduleClose() {
    window.clearTimeout(this.openTimer);
    this.closeTimer = window.setTimeout(() => this.closePanel(false), 300);
  }

  cancelClose() {
    window.clearTimeout(this.closeTimer);
  }

  openPanel(trigger) {
    const panelName = trigger.dataset.hrPanelTrigger;
    const panel = this.querySelector(`[data-hr-panel="${panelName}"]`);
    if (!panel) return;

    if (panelName === 'producers') this.resetProducerOverview(panel);
    if (panelName === 'incontournables') {
      const activeCollectionLink = panel.querySelector('[data-hr-collection-tab].is-active')
        || panel.querySelector('[data-hr-collection-tab]');
      panel.hrActivateCollectionTab?.(activeCollectionLink);
    }

    window.clearTimeout(this.openTimer);
    this.cancelClose();
    this.panels.forEach((candidate) => candidate.classList.toggle('is-open', candidate === panel));
    this.desktopTriggers.forEach((candidate) => candidate.setAttribute('aria-expanded', candidate === trigger ? 'true' : 'false'));
    this.classList.add('has-open-panel');
    this.activePanel = panel;
    this.activeTrigger = trigger;
  }

  closePanel(returnFocus) {
    window.clearTimeout(this.openTimer);
    this.cancelClose();
    this.panels.forEach((panel) => panel.classList.remove('is-open'));
    this.desktopTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.classList.remove('has-open-panel');
    const trigger = this.activeTrigger;
    this.activePanel = null;
    this.activeTrigger = null;
    if (returnFocus && trigger) trigger.focus();
  }

  resetProducerOverview(panel) {
    this.ensureProducerOverview(panel);
    panel.querySelectorAll('[data-country-tab]').forEach((tab) => tab.setAttribute('aria-selected', 'false'));
    panel.querySelectorAll('[data-country-panel]').forEach((countryPanel) => {
      countryPanel.classList.remove('is-active');
    });
    const content = panel.querySelector('.hr-mega-panel__content');
    content?.classList.add('is-overview');
    content?.classList.toggle('has-overview', Boolean(content.querySelector('[data-country-overview]')));
  }

  ensureProducerOverview(panel) {
    const content = panel.querySelector('.hr-mega-panel__content');
    if (!content || content.querySelector('[data-country-overview]')) return;

    const overview = document.createElement('div');
    overview.className = 'hr-producer-overview';
    overview.setAttribute('role', 'tabpanel');
    overview.setAttribute('data-country-overview', '');

    ['france', 'allemagne', 'autriche', 'espagne'].forEach((country, index) => {
      const sourcePanel = content.querySelector(`[data-country-panel="${country}"]`);
      const tab = panel.querySelector(`[data-country-tab="${country}"]`);
      if (!sourcePanel || !tab) return;

      const title = tab.textContent.trim();
      const sourceLink = sourcePanel.querySelector('.hr-producer-panel__footer a');
      const sourceCards = [...sourcePanel.querySelectorAll('.hr-producer-card')];
      const group = document.createElement('section');
      group.className = `hr-producer-overview__group hr-producer-overview__group--${index < 2 ? 'major' : 'compact'}`;

      if (index < 2) {
        const heading = document.createElement('div');
        heading.className = 'hr-producer-overview__heading';
        const headingTitle = document.createElement('h3');
        headingTitle.textContent = title;
        heading.append(headingTitle);
        if (sourceLink) heading.append(sourceLink.cloneNode(true));

        const grid = document.createElement('div');
        grid.className = 'hr-producer-grid hr-producer-grid--overview';
        sourceCards.slice(0, 6).forEach((card) => grid.append(card.cloneNode(true)));
        group.append(heading, grid);
      } else {
        const logoWrap = document.createElement('span');
        logoWrap.className = 'hr-producer-overview__compact-logos';
        const grid = document.createElement('div');
        grid.className = 'hr-producer-grid hr-producer-grid--overview';
        if (sourceCards[0]) grid.append(sourceCards[0].cloneNode(true));
        logoWrap.append(grid);

        const copy = document.createElement('span');
        copy.className = 'hr-producer-overview__compact-copy';
        const copyTitle = document.createElement('strong');
        copyTitle.textContent = title;
        copy.append(copyTitle);
        if (sourceLink) copy.append(sourceLink.cloneNode(true));
        group.append(logoWrap, copy);
      }

      overview.append(group);
    });

    content.prepend(overview);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.mobileDrawer?.classList.contains('is-open')) this.closeMobile();
      else this.closePanel(true);
    }
  }

  bindProducerTabs() {
    this.querySelectorAll('[data-hr-producer-tabs]').forEach((tabset) => {
      const tabs = [...tabset.querySelectorAll('[data-country-tab]')];
      const container = tabset.closest('[data-hr-panel]') || this;
      const activate = (tab) => {
        const country = tab.dataset.countryTab;
        container.querySelector('.hr-mega-panel__content')?.classList.remove('is-overview');
        tabs.forEach((candidate) => candidate.setAttribute('aria-selected', candidate === tab ? 'true' : 'false'));
        container.querySelectorAll('[data-country-panel]').forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.countryPanel === country);
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('mouseenter', () => activate(tab));
        tab.addEventListener('focus', () => activate(tab));
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowUp', 'ArrowDown', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'ArrowRight') {
            container.querySelector('[data-country-panel].is-active a')?.focus();
            return;
          }
          const nextIndex = event.key === 'ArrowDown'
            ? (index + 1) % tabs.length
            : (index - 1 + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          activate(tabs[nextIndex]);
        });
      });
    });
  }

  bindCollectionTabs() {
    this.querySelectorAll('[data-hr-collection-tabs]').forEach((tabset) => {
      const links = [...tabset.querySelectorAll('[data-hr-collection-tab]')];
      const container = tabset.closest('[data-hr-panel]') || this;
      const panels = [...container.querySelectorAll('[data-hr-collection-panel]')];

      const activate = (link) => {
        if (!link) return;
        const panelKey = link.dataset.hrCollectionTab;
        links.forEach((candidate) => candidate.classList.toggle('is-active', candidate === link));
        panels.forEach((panel) => {
          const isActive = panel.dataset.hrCollectionPanel === panelKey;
          panel.classList.toggle('is-active', isActive);
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) this.loadCollectionProducts(link, panel);
        });
      };

      container.hrActivateCollectionTab = activate;

      links.forEach((link) => {
        link.addEventListener('mouseenter', () => activate(link));
        link.addEventListener('focus', () => activate(link));
      });
    });
  }

  prefetchCollectionProducts() {
    if (this.collectionPrefetchStarted) return;
    this.collectionPrefetchStarted = true;

    this.querySelectorAll('[data-hr-collection-tabs]').forEach((tabset) => {
      const container = tabset.closest('[data-hr-panel]') || this;
      const panels = [...container.querySelectorAll('[data-hr-collection-panel]')];

      tabset.querySelectorAll('[data-hr-collection-tab]').forEach((link) => {
        const panel = panels.find((candidate) => (
          candidate.dataset.hrCollectionPanel === link.dataset.hrCollectionTab
        ));
        if (panel) this.loadCollectionProducts(link, panel);
      });
    });
  }

  preloadProductImages(products) {
    products.forEach((product) => {
      const imageUrl = product.featured_image || product.images?.[0];
      if (!imageUrl || this.preloadedProductImages.has(imageUrl)) return;

      this.preloadedProductImages.add(imageUrl);
      const image = new Image();
      image.src = imageUrl;
    });
  }

  async loadCollectionProducts(link, panel) {
    const collectionUrl = new URL(link.href, window.location.origin).toString();
    if (panel.dataset.loadedCollectionUrl === collectionUrl) return;

    panel.classList.add('is-loading');
    panel.setAttribute('aria-busy', 'true');

    try {
      if (!this.collectionProductCache.has(collectionUrl)) {
        this.collectionProductCache.set(collectionUrl, this.fetchCollectionProducts(collectionUrl));
      }
      const products = await this.collectionProductCache.get(collectionUrl);
      this.preloadProductImages(products);
      if (!products.length) {
        panel.innerHTML = '<p class="hr-collection-products__empty">Aucun produit ne correspond à cette sélection.</p>';
        panel.dataset.loadedCollectionUrl = collectionUrl;
        return;
      }

      panel.innerHTML = `<div class="hr-product-grid">${products.map((product) => this.renderDynamicProductCard(product)).join('')}</div>`;
      panel.dataset.loadedCollectionUrl = collectionUrl;
      this.bindAddToCart(panel);
    } catch (error) {
      console.warn('Unable to load filtered collection products for the header.', error);
    } finally {
      panel.classList.remove('is-loading');
      panel.removeAttribute('aria-busy');
    }
  }

  async fetchCollectionProducts(collectionUrl) {
    const collectionResponse = await fetch(collectionUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!collectionResponse.ok) throw new Error(`Collection request failed: ${collectionResponse.status}`);

    const collectionHtml = await collectionResponse.text();
    const collectionDocument = new DOMParser().parseFromString(collectionHtml, 'text/html');
    const productGrid = collectionDocument.querySelector('#product-grid');
    if (!productGrid) throw new Error('Filtered product grid was not found.');

    const handles = [];
    productGrid.querySelectorAll('a[href*="/products/"]').forEach((productLink) => {
      if (handles.length >= 3) return;
      const productUrl = new URL(productLink.href, window.location.origin);
      const handle = productUrl.pathname.split('/products/')[1]?.split('/')[0];
      if (handle && !handles.includes(handle)) handles.push(handle);
    });

    const rootUrl = window.Shopify?.routes?.root || '/';
    return Promise.all(handles.map(async (handle) => {
      const productResponse = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!productResponse.ok) throw new Error(`Product request failed: ${productResponse.status}`);
      return productResponse.json();
    }));
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  formatProductPrice(price) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'fr-FR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(price || 0) / 100);
  }

  renderDynamicProductCard(product) {
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    const title = this.escapeHtml(product.title);
    const vendor = this.escapeHtml(product.vendor);
    const imageUrl = this.escapeHtml(product.featured_image || product.images?.[0] || '');
    const availableVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];
    const isDefaultVariant = product.variants?.length === 1 && product.options?.length === 1;
    const price = this.formatProductPrice(product.price);
    const compareAtPrice = Number(product.compare_at_price || 0);
    const priceMarkup = compareAtPrice > Number(product.price)
      ? `<s>${this.escapeHtml(this.formatProductPrice(compareAtPrice))}</s><span>${this.escapeHtml(price)}</span>`
      : `<span>${this.escapeHtml(price)}</span>`;

    let buttonMarkup = '<button class="hr-product-card__button" type="button" disabled>Épuisé</button>';
    if (availableVariant?.available && isDefaultVariant) {
      buttonMarkup = `<button class="hr-product-card__button" type="button" data-hr-add-to-cart data-variant-id="${availableVariant.id}"><span>Ajouter au panier</span><span class="hr-product-card__loading" hidden aria-hidden="true"></span></button>`;
    } else if (availableVariant?.available) {
      buttonMarkup = `<a class="hr-product-card__button" href="${productUrl}">Découvrir</a>`;
    }

    const imageMarkup = imageUrl
      ? `<img class="hr-product-card__image" src="${imageUrl}" alt="${title}" loading="lazy" width="440" height="440">`
      : '';

    return `<article class="hr-product-card" data-hr-product-card>
      <a class="hr-product-card__media" href="${productUrl}" aria-label="${title}">${imageMarkup}</a>
      <div class="hr-product-card__content">
        <a class="hr-product-card__title" href="${productUrl}">${title}</a>
        <p class="hr-product-card__meta">${vendor}</p>
        <div class="hr-product-card__price">${priceMarkup}</div>
        ${buttonMarkup}
        <p class="hr-product-card__error" data-hr-product-error hidden></p>
      </div>
    </article>`;
  }

  bindCountrySelectors() {
    this.querySelectorAll('[data-hr-country-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  bindAddToCart(root = this) {
    root.querySelectorAll('[data-hr-add-to-cart]').forEach((button) => {
      if (button.dataset.hrAddToCartBound === 'true') return;
      button.dataset.hrAddToCartBound = 'true';
      button.addEventListener('click', async () => {
        if (button.disabled || button.classList.contains('is-loading')) return;
        const card = button.closest('[data-hr-product-card]');
        const error = card?.querySelector('[data-hr-product-error]');
        const loader = button.querySelector('.hr-product-card__loading');
        const cartNotification = document.querySelector('cart-notification');

        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
        loader?.removeAttribute('hidden');
        if (error) error.hidden = true;

        try {
          const body = {
            id: Number(button.dataset.variantId),
            quantity: 1,
            sections: cartNotification?.getSectionsToRender().map((section) => section.id) || ['cart-icon-bubble'],
            sections_url: window.location.pathname
          };
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
          });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || 'Impossible d’ajouter ce produit.');

          this.closePanel(false);
          if (cartNotification?.renderContents && state.sections) cartNotification.renderContents(state);
          else window.location.href = window.routes?.cart_url || '/cart';
        } catch (caughtError) {
          if (error) {
            error.textContent = caughtError.message;
            error.hidden = false;
          }
        } finally {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
          loader?.setAttribute('hidden', '');
        }
      });
    });
  }

  bindSliders() {
    this.querySelectorAll('[data-hr-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-hr-slider-track]');
      slider.querySelectorAll('[data-hr-slider-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!track) return;
          const direction = Number(button.dataset.hrSliderDirection);
          const card = track.querySelector('.hr-article-card');
          if (!card) return;

          const columnGap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
          const step = card.getBoundingClientRect().width + columnGap;
          const currentIndex = Math.round(track.scrollLeft / step);
          const visibleCards = Math.max(1, Math.floor((track.clientWidth + columnGap) / step));
          const maxIndex = Math.max(0, track.children.length - visibleCards);
          const targetIndex = Math.min(maxIndex, Math.max(0, currentIndex + direction));

          track.scrollTo({ left: targetIndex * step, behavior: 'smooth' });
        });
      });
    });
  }

  bindMobile() {
    this.mobileToggle = this.querySelector('[data-hr-mobile-open]');
    this.mobileToggle?.addEventListener('click', () => {
      if (this.mobileDrawer?.classList.contains('is-open')) this.closeMobile();
      else this.openMobile();
    });
    this.querySelectorAll('[data-hr-mobile-close]').forEach((button) => button.addEventListener('click', () => this.closeMobile()));

    this.querySelectorAll('[data-mobile-accordion-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const name = trigger.dataset.mobileAccordionTrigger;
        const target = this.querySelector(`[data-mobile-accordion="${name}"]`);
        const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.querySelectorAll('[data-mobile-accordion-trigger]').forEach((candidate) => candidate.setAttribute('aria-expanded', 'false'));
        this.querySelectorAll('[data-mobile-accordion]').forEach((candidate) => candidate.classList.remove('is-open'));
        if (!wasOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          target?.classList.add('is-open');
          window.setTimeout(() => trigger.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 210);
        }
      });
    });

    const sheet = this.querySelector('[data-mobile-language-sheet]');
    const sheetBackdrop = this.querySelector('[data-mobile-language-backdrop]');
    const toggleSheet = (open) => {
      this.classList.toggle('has-open-language', open);
      this.stickySection?.classList.toggle('hr-sticky-section--raised', open);
      sheet?.classList.toggle('is-open', open);
      sheetBackdrop?.classList.toggle('is-open', open);
      sheet?.setAttribute('aria-hidden', open ? 'false' : 'true');
    };
    this.querySelector('[data-mobile-language-open]')?.addEventListener('click', () => toggleSheet(true));
    this.querySelectorAll('[data-mobile-language-close]').forEach((button) => button.addEventListener('click', () => toggleSheet(false)));
  }

  openMobile() {
    const headerBar = this.querySelector('.hr-header__bar');
    const drawerTop = Math.max(0, Math.round(headerBar?.getBoundingClientRect().bottom || 56));
    this.mobileDrawer?.style.setProperty('--hr-mobile-drawer-top', `${drawerTop}px`);
    this.mobileDrawer?.classList.add('is-open');
    this.mobileDrawer?.setAttribute('aria-hidden', 'false');
    this.mobileToggle?.setAttribute('aria-expanded', 'true');
    this.mobileToggle?.setAttribute('aria-label', this.mobileToggle.dataset.closeLabel || 'Fermer');
    this.setMobileScreen('main');
    document.body.classList.add('hr-mobile-menu-open');
  }

  closeMobile() {
    const languageSheet = this.querySelector('[data-mobile-language-sheet]');
    this.classList.remove('has-open-language');
    languageSheet?.classList.remove('is-open');
    languageSheet?.setAttribute('aria-hidden', 'true');
    this.querySelector('[data-mobile-language-backdrop]')?.classList.remove('is-open');
    this.mobileDrawer?.classList.remove('is-open');
    this.mobileDrawer?.setAttribute('aria-hidden', 'true');
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    this.mobileToggle?.setAttribute('aria-label', this.mobileToggle.dataset.openLabel || 'Menu');
    document.body.classList.remove('hr-mobile-menu-open');
    this.mobileToggle?.focus();
  }

  setMobileScreen(screen) {
    if (this.mobileDrawer) this.mobileDrawer.dataset.screen = screen;
  }
}

if (!customElements.get('header-redesign')) {
  customElements.define('header-redesign', HeaderRedesign);
}
